import { useCallback, useState } from "react";
import type { ManualBodyStep } from "@arc/contracts";

/**
 * The main element's body as COLLAPSIBLE SECTIONS (ADR-027 §11, owner "pure legacy" 2026-09-09).
 *
 * The legacy `CreateMainSettingElement` builds one collapsible per setting, by main-element TYPE; every section starts
 * collapsed unless its key was remembered in `localStorage["arlms_expanded_settings"]`, and "Alle ausklappen / Alle
 * einklappen" flips them all. This module is that rule + that memory, nothing visual.
 */

/**
 * Section label KEYS.
 *
 * These were the legacy German strings inline, with a note that "i18n can lift them later" — this is later. The
 * German in the catalogue is that text verbatim; the English is new, because the legacy never had any.
 */
export const SECTION_LABELS = {
  standard: "manualEditor.sections.standard",
  completed: "manualEditor.sections.completed",
  failed: "manualEditor.sections.failed",
  checklistDescription: "manualEditor.sections.checklistDescription",
  checklistAnswers: "manualEditor.sections.checklistAnswers",
  question: "manualEditor.sections.question",
  answers: "manualEditor.sections.answers",
  scoreLevel: "manualEditor.sections.scoreLevel",
  expandAll: "manualEditor.sections.expandAll",
  collapseAll: "manualEditor.sections.collapseAll",
} as const;
export type BodySectionKey = Exclude<keyof typeof SECTION_LABELS, "expandAll" | "collapseAll">;

/** Settings simple enough for a row with their control. Read-only ones keep their collapsible, which says why and has the room. */
const INLINE_SETTINGS = new Set(["MeshColor", "BackgroundPanel", "LeftRightDirection", "Form", "AnimationState", "AreaSize"]);
export function isInlineSetting(name: string, canSet: boolean): boolean {
  return canSet && INLINE_SETTINGS.has(name);
}

/**
 * Which sections a step's main element has, in the legacy order (`ARLMS_SettingUIs.js:470-516`):
 *  - ShowScore: Completed · Failed · its score level.
 *  - Question / QuestionWithScore: Standard · Completed · Failed · Frage · Antworten.
 *  - Checklist: Standard · Completed · Checkbox Beschreibung · Checkboxen — NO Failed (`:502`).
 *  - the plain main element (`regular`): its own text + media.
 * PLUS the content fallback (plan-panel Medium): Interaction and Sensor main elements normalise to `regular` in our
 * model, so a `regular` step ALSO gets Completed / Failed whenever that text exists — a migrated sensor step must not
 * lose its feedback editors (the card's earlier `usesExtraStates` rule, kept).
 */
export function sectionsForStep(step: Pick<ManualBodyStep, "type" | "text" | "completedText" | "failedText" | "media" | "completedMedia" | "failedMedia">): BodySectionKey[] {
  switch (step.type) {
    case "checklist":
      return ["standard", "completed", "checklistDescription", "checklistAnswers"];
    case "question":
    case "question_with_score":
      return ["standard", "completed", "failed", "question", "answers"];
    case "show_score":
      // No standard section in the legacy — but an existing text OR MEDIA must stay reachable (diff-panel Medium #2:
      // the media tile lives in the standard section, and `Image`/`Video` are filtered out of the settings list).
      return [...(step.text != null || step.media != null ? (["standard"] as const) : []), "completed", "failed", "scoreLevel"];
    default: {
      // Text OR media: a state whose text was cleared still holds its attachment, and the media tile lives in the section
      // (diff-panel B Medium #1) — the same guard the score branch uses.
      const out: BodySectionKey[] = ["standard"];
      if (step.completedText != null || step.completedMedia != null) out.push("completed");
      if (step.failedText != null || step.failedMedia != null) out.push("failed");
      return out;
    }
  }
}

/** The remembered open sections, `"<elementId>:<section>"` keys like the legacy's `"<id>_<setting>"` list. */
export const EXPANDED_SECTIONS_STORAGE_KEY = "arc_expanded_sections";

function readExpanded(): Set<string> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_SECTIONS_STORAGE_KEY);
    const arr: unknown = raw == null ? [] : JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}
function writeExpanded(keys: Set<string>): void {
  try {
    window.localStorage.setItem(EXPANDED_SECTIONS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    /* storage full / unavailable — the state simply isn't remembered, exactly like the legacy */
  }
}

export interface SectionState {
  isOpen: (key: string) => boolean;
  setOpen: (key: string, open: boolean) => void;
  /** The "Alle ausklappen / Alle einklappen" toggle — every key of this element at once. */
  setAll: (open: boolean) => void;
  anyOpen: boolean;
}

/**
 * Open/closed state for one element's sections — the main element's body sections AND every other element's
 * settings (the legacy collapses each setting the same way, under the same toggle): collapsed by default, remembered
 * per element + key across reloads (owner: pure legacy). Keys are the body section keys or setting NAMES.
 */
export function useBodySections(elementId: number | null, keys: readonly string[]): SectionState {
  const [expanded, setExpanded] = useState<Set<string>>(() => readExpanded());
  const storageKey = useCallback((k: string) => `${String(elementId ?? "none")}:${k}`, [elementId]);
  const isOpen = useCallback((k: string) => expanded.has(storageKey(k)), [expanded, storageKey]);
  // Every write MERGES into the latest persisted list rather than overwriting it with this instance's snapshot: several
  // instances live at once (the main element's card + a selected non-main element's), and a stale snapshot would
  // silently drop what the other remembered (diff-panel consensus Medium #3).
  const setOpen = useCallback(
    (k: string, open: boolean) => {
      const next = readExpanded();
      if (open) next.add(storageKey(k));
      else next.delete(storageKey(k));
      writeExpanded(next);
      setExpanded(next);
    },
    [storageKey],
  );
  const setAll = useCallback(
    (open: boolean) => {
      const next = readExpanded();
      for (const k of keys) {
        if (open) next.add(storageKey(k));
        else next.delete(storageKey(k));
      }
      writeExpanded(next);
      setExpanded(next);
    },
    [keys, storageKey],
  );
  const anyOpen = keys.some((k) => expanded.has(storageKey(k)));
  return { isOpen, setOpen, setAll, anyOpen };
}
